import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { HeartIcon, ShieldCheckIcon, UsersIcon, AwardIcon, TrendingUpIcon } from 'lucide-react';
export function AboutUs() {
  const stats = [{
    value: '500K+',
    label: 'Happy Customers'
  }, {
    value: '10K+',
    label: 'Products'
  }, {
    value: '15+',
    label: 'Years Experience'
  }, {
    value: '98%',
    label: 'Satisfaction Rate'
  }];
  const values = [{
    icon: <HeartIcon className="w-8 h-8" data-id="element-5730" data-mp="2ftmi1wuq0t3u" />,
    title: 'Customer Care',
    description: 'Your health and satisfaction are our top priorities. We go above and beyond to ensure quality service.'
  }, {
    icon: <ShieldCheckIcon className="w-8 h-8" data-id="element-5731" data-mp="2omfzo0xn44kv" />,
    title: 'Quality Assurance',
    description: '100% authentic products from verified suppliers. Every item is checked for quality and authenticity.'
  }, {
    icon: <UsersIcon className="w-8 h-8" data-id="element-5732" data-mp="1rwymz208pi9o" />,
    title: 'Expert Team',
    description: 'Licensed pharmacists and healthcare professionals ready to assist you with expert advice.'
  }, {
    icon: <AwardIcon className="w-8 h-8" data-id="element-5733" data-mp="20xwsjoqbtxud" />,
    title: 'Excellence',
    description: 'Committed to excellence in everything we do, from product selection to customer service.'
  }];
  const milestones = [{
    year: '2008',
    event: 'Innova Pharmacy Founded'
  }, {
    year: '2012',
    event: 'Expanded to 10 Locations'
  }, {
    year: '2015',
    event: 'Launched Online Platform'
  }, {
    year: '2018',
    event: 'Reached 100K Customers'
  }, {
    year: '2020',
    event: '24/7 Service Introduced'
  }, {
    year: '2023',
    event: 'AI-Powered Healthcare Solutions'
  }];
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  return <HelmetProvider data-id="element-5734" data-mp="1vbayc5ss7vmv">
      <SEO title="About Us" description="Learn about Innova Pharmacy - your trusted online healthcare partner in Saudi Arabia. Discover our mission, values, and commitment to quality healthcare." canonical="/about-us" data-id="element-5735" data-mp="1v8eyud1acywb" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-5736" data-mp="17loyrh8hzeec">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-5737" data-mp="1cji1g0aldq9v">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} disableShrink={true} data-id="element-5738" data-mp="1ci70e88j6wmg" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-5739" data-mp="v102dqe96z49">
          {/* Mobile Header Overlay */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-5740" data-mp="m1mtozxyeekl">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-5741" data-mp="m2xwawfnxga4" />
          </div>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'About Us'
        }]} title="About Us" description="Dedicated to providing accessible, quality healthcare products and services to communities across Saudi Arabia since 2008." data-id="element-5742" data-mp="7di91qfcbsz6" />
          {/* Stats Section */}
          <div className="container mx-auto px-4 py-8" data-id="element-5743" data-mp="22hpzyt4unw6n">
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200" data-id="element-5744" data-mp="1onff4mvz4m5v">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-5745" data-mp="1tms6nkxsihes">
                {stats.map((stat, index) => <div key={index} className="text-center" data-id="element-5746" data-mp="1epj5jziopz9v" data-mpi={index}>
                    <div className="text-3xl md:text-4xl font-bold text-[#1E2B55] mb-2" data-id="element-5747" data-mp="1jpx66whyi6xv">
                      {stat.value}
                    </div>
                    <div className="text-gray-600 font-medium" data-id="element-5748" data-mp="15rh097rx4kgs">
                      {stat.label}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Our Story */}
          <div className="container mx-auto px-4 py-16" data-id="element-5749" data-mp="352gqi3fqz0bd">
            <div className="max-w-4xl mx-auto" data-id="element-5750" data-mp="351yvtb8qgu5k">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center" data-id="element-5751" data-mp="3f65m2i52y647">
                Our Story
              </h2>
              <div className="prose prose-lg max-w-none text-gray-600" data-id="element-5752" data-mp="3a95gs9g217e5">
                <p className="text-lg leading-relaxed mb-4" data-id="element-5753" data-mp="3nz8ycn3zocx9">
                  Founded in 2008, Innova Pharmacy began with a simple mission:
                  to make quality healthcare accessible to everyone in Saudi
                  Arabia. What started as a single pharmacy in Riyadh has grown
                  into a trusted healthcare partner serving hundreds of
                  thousands of customers across the Kingdom.
                </p>
                <p className="text-lg leading-relaxed mb-4" data-id="element-5754" data-mp="aq4gunrj1ew7">
                  Over the years, we've expanded our services beyond traditional
                  pharmacy offerings to include online consultations,
                  prescription management, and a comprehensive range of health
                  and wellness products. Our commitment to innovation and
                  customer care has made us a leader in the healthcare retail
                  industry.
                </p>
                <p className="text-lg leading-relaxed" data-id="element-5755" data-mp="girwueuj0hsm">
                  Today, we continue to evolve, embracing new technologies and
                  expanding our services to meet the changing needs of our
                  customers while staying true to our core values of quality,
                  trust, and care.
                </p>
              </div>
            </div>
          </div>
          {/* Vision & Mission */}
          <div className="bg-white py-16" data-id="element-5756" data-mp="146jkufpfij9q">
            <div className="container mx-auto px-4" data-id="element-5757" data-mp="147kzeu5nlnw7">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-5758" data-mp="195394mrhw759">
                <div className="bg-gradient-to-br from-[#1E2B55] to-[#2a3a6f] rounded-xl p-8 text-white" data-id="element-5759" data-mp="1e4fut46mr869">
                  <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mb-4" data-id="element-5760" data-mp="1i5rn5hgw27i5">
                    <TrendingUpIcon className="w-8 h-8" data-id="element-5761" data-mp="32obwaxnfi4pj" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4" data-id="element-5762" data-mp="2ymqa2lp08e2o">Our Vision</h3>
                  <p className="text-blue-100 leading-relaxed" data-id="element-5763" data-mp="2yokvs283oa7t">
                    To be the most trusted and innovative healthcare partner in
                    Saudi Arabia, empowering individuals to take control of
                    their health through accessible, quality products and
                    services.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-[#22c55e] to-[#16a34a] rounded-xl p-8 text-white" data-id="element-5764" data-mp="3heaqkpbv4vzq">
                  <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mb-4" data-id="element-5765" data-mp="3ckopzviz4ika">
                    <HeartIcon className="w-8 h-8" data-id="element-5766" data-mp="37l2b7hnhyu9d" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4" data-id="element-5767" data-mp="8yr0u0aehvpf">Our Mission</h3>
                  <p className="text-green-100 leading-relaxed" data-id="element-5768" data-mp="466e1ipwwive">
                    To provide exceptional healthcare products and services with
                    integrity, compassion, and innovation, making a positive
                    impact on the wellbeing of our communities.
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Core Values */}
          <div className="container mx-auto px-4 py-16" data-id="element-5769" data-mp="33pzc2igbl3ep">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center" data-id="element-5770" data-mp="3rf0cpjiwva9f">
              Our Core Values
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-5771" data-mp="7618dexeso8">
              {values.map((value, index) => <div key={index} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow" data-id="element-5772" data-mp="3ig6lk8ocnc39" data-mpi={index}>
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center text-[#1E2B55] mb-4" data-id="element-5773" data-mp="26xc940yk1967">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-5774" data-mp="23fybk65lv696">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-5775" data-mp="1zf4nncs5xrga">
                    {value.description}
                  </p>
                </div>)}
            </div>
          </div>
          {/* Timeline */}
          <div className="bg-white py-16" data-id="element-5776" data-mp="1kqhvgahft57n">
            <div className="container mx-auto px-4" data-id="element-5777" data-mp="1krjhtb3wle4o">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center" data-id="element-5778" data-mp="1frf1bl2zwjeg">
                Our Journey
              </h2>
              <div className="max-w-4xl mx-auto" data-id="element-5779" data-mp="1uhcu6ebvdx9z">
                <div className="relative" data-id="element-5780" data-mp="1ui4gsznhoko4">
                  {/* Timeline line */}
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gray-200" data-id="element-5781" data-mp="1po9v5qobpvme"></div>
                  {/* Timeline items */}
                  <div className="space-y-8" data-id="element-5782" data-mp="wzw1to86jnh6">
                    {milestones.map((milestone, index) => <div key={index} className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-5783" data-mp="1sj4d1g8d4epm" data-mpi={index}>
                        <div className="flex-1 md:text-right md:pr-8" data-id="element-5784" data-mp="1ikoif3a4mnbd">
                          {index % 2 === 0 && <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm" data-id="element-5785" data-mp="1dp982lbbv9jp">
                              <div className="text-2xl font-bold text-[#1E2B55] mb-2" data-id="element-5786" data-mp="19nnvl12pgxa1">
                                {milestone.year}
                              </div>
                              <div className="text-gray-600" data-id="element-5787" data-mp="zw9s6i7mvdbm">
                                {milestone.event}
                              </div>
                            </div>}
                        </div>
                        <div className="hidden md:flex w-4 h-4 bg-[#1E2B55] rounded-full border-4 border-white shadow-md z-10" data-id="element-5788" data-mp="pzeyqdq7w347"></div>
                        <div className="flex-1 md:pl-8" data-id="element-5789" data-mp="2xk3vewjpri0i">
                          {index % 2 !== 0 && <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm" data-id="element-5790" data-mp="31jw7vkc8a8b5">
                              <div className="text-2xl font-bold text-[#1E2B55] mb-2" data-id="element-5791" data-mp="36l22jnpg85nb">
                                {milestone.year}
                              </div>
                              <div className="text-gray-600" data-id="element-5792" data-mp="2dvv40opzvg22">
                                {milestone.event}
                              </div>
                            </div>}
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Certifications */}
          <div className="container mx-auto px-4 py-16" data-id="element-5793" data-mp="1v5n3medoj3pk">
            <div className="bg-gradient-to-br from-[#1E2B55] to-[#2a3a6f] rounded-xl p-8 md:p-12 text-white text-center" data-id="element-5794" data-mp="1v30x8i2iyu8x">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-5795" data-mp="1s30g0mjmff06">
                Certified & Trusted
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto" data-id="element-5796" data-mp="1o0m0ocf5uxdn">
                Innova Pharmacy is licensed and regulated by the Saudi Food and
                Drug Authority (SFDA) and adheres to the highest standards of
                pharmaceutical practice.
              </p>
              <div className="flex flex-wrap justify-center gap-6" data-id="element-5797" data-mp="193mm24lw649o">
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg px-6 py-4" data-id="element-5798" data-mp="152sy5b8g8pgs">
                  <div className="text-2xl font-bold" data-id="element-5799" data-mp="1j2khlrhqiy26">SFDA</div>
                  <div className="text-sm text-blue-100" data-id="element-5800" data-mp="1j45c5h41uh01">Licensed</div>
                </div>
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg px-6 py-4" data-id="element-5801" data-mp="1e2pnlf2nxscw">
                  <div className="text-2xl font-bold" data-id="element-5802" data-mp="2ugfuetl62uq8">ISO 9001</div>
                  <div className="text-sm text-blue-100" data-id="element-5803" data-mp="2pj60p1qqvwwl">Certified</div>
                </div>
                <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg px-6 py-4" data-id="element-5804" data-mp="bb55gohh6b10">
                  <div className="text-2xl font-bold" data-id="element-5805" data-mp="g9q6gq4jwyqi">GMP</div>
                  <div className="text-sm text-blue-100" data-id="element-5806" data-mp="g7vhn0q5smh5">Compliant</div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer data-id="element-5807" data-mp="78i9qchoo5yj" />
        <MobileFooterNav data-id="element-5808" data-mp="7a34a23zzowe" />
        <PharmacistChat data-id="element-5809" data-mp="7be6vylpiqlx" />
        <div className="h-16 md:hidden" data-id="element-5810" data-mp="uz4f1w0azc4u"></div>
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-5811" data-mp="uze67mx33pc4" />
      </div>
    </HelmetProvider>;
}